import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar/Sidebar";
import { Topbar } from "../../components/Topbar/Topbar";
import axiosClient from "../../axiosClient";
import toast from "react-hot-toast";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import BootstrapButton from "react-bootstrap/Button";
import "./ModuleStatsPage.scss";
import { Loader } from "rsuite";

interface ModuleStats {
  id: number;
  name: string;
  numberOfQuestions: number;
  numberOfQuestionsCompleted: number;
  correctlyAnsweredQuestionsCount: number;
  incorrectlyAnsweredQuestionsCount: number;
  numberOfStarredQuestions: number;
  numberOfFlaggedQuestions: number;
}

interface AggregatedStats {
  moduleIds: number[];
  moduleName: string;
  numberOfQuestions: number;
  numberOfQuestionsCompleted: number;
  correctlyAnsweredQuestionsCount: number;
  incorrectlyAnsweredQuestionsCount: number;
  numberOfStarredQuestions: number;
  numberOfFlaggedQuestions: number;
}

const ModuleStatsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const moduleIds = searchParams.get("moduleIds");

        if (!moduleIds) {
          toast.error("No modules specified");
          setIsLoading(false);
          return;
        }

        const response = await axiosClient.get(`/module-stats?moduleIds=${moduleIds}`);
        const moduleStatsArray: ModuleStats[] = response.data;

        // Aggregate stats from all modules
        const aggregated: AggregatedStats = {
          moduleIds: moduleStatsArray.map(m => m.id),
          moduleName: getAggregatedModuleName(moduleStatsArray),
          numberOfQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfQuestions, 0),
          numberOfQuestionsCompleted: moduleStatsArray.reduce((sum, m) => sum + m.numberOfQuestionsCompleted, 0),
          correctlyAnsweredQuestionsCount: moduleStatsArray.reduce((sum, m) => sum + m.correctlyAnsweredQuestionsCount, 0),
          incorrectlyAnsweredQuestionsCount: moduleStatsArray.reduce((sum, m) => sum + m.incorrectlyAnsweredQuestionsCount, 0),
          numberOfStarredQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfStarredQuestions, 0),
          numberOfFlaggedQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfFlaggedQuestions, 0),
        };

        setStats(aggregated);
        setIsLoading(false);
      } catch (err) {
        toast.error("Error loading module statistics. Please seek support!");
        console.error(err);
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [searchParams]);

  const getAggregatedModuleName = (moduleStatsArray: ModuleStats[]): string => {
    return moduleStatsArray.length === 1
      ? moduleStatsArray[0].name
      : moduleStatsArray.length === 2
        ? `${moduleStatsArray[0].name} and ${moduleStatsArray[1].name}`
        : moduleStatsArray.length > 2
          ? moduleStatsArray
                .map(m => m.name)
                .slice(0, -1)
                .join(", ") +
                ", and " +
                moduleStatsArray[moduleStatsArray.length - 1].name
          : `${moduleStatsArray.length} Modules`;
  }

  const handleResetProgress = async () => {
    const moduleText = stats && stats.moduleIds.length === 1 ? "this module" : "these modules";
    const confirmation = window.confirm(
      `Are you sure you want to clear progress for ${moduleText}?`
    );

    if (confirmation && stats) {
      try {
        const moduleIdsParam = stats.moduleIds.join(",");
        const res = await axiosClient.put(`/reset-progress?moduleIds=${moduleIdsParam}`);

        if (res.status === 200) {
          toast.success("Module progress reset successfully");
          // Refresh stats after reset
          const response = await axiosClient.get(`/module-stats?moduleIds=${moduleIdsParam}`);
          const moduleStatsArray: ModuleStats[] = response.data;

          // Re-aggregate stats
          const aggregated: AggregatedStats = {
            moduleIds: moduleStatsArray.map(m => m.id),
            moduleName: getAggregatedModuleName(moduleStatsArray),
            numberOfQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfQuestions, 0),
            numberOfQuestionsCompleted: moduleStatsArray.reduce((sum, m) => sum + m.numberOfQuestionsCompleted, 0),
            correctlyAnsweredQuestionsCount: moduleStatsArray.reduce((sum, m) => sum + m.correctlyAnsweredQuestionsCount, 0),
            incorrectlyAnsweredQuestionsCount: moduleStatsArray.reduce((sum, m) => sum + m.incorrectlyAnsweredQuestionsCount, 0),
            numberOfStarredQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfStarredQuestions, 0),
            numberOfFlaggedQuestions: moduleStatsArray.reduce((sum, m) => sum + m.numberOfFlaggedQuestions, 0),
          };

          setStats(aggregated);
        } else {
          toast.error("Error occurred resetting module progress");
        }
      } catch (err) {
        toast.error("Error occurred resetting module progress");
        console.error(err);
      }
    }
  };

  const handlePrimaryAction = () => {
    if (!stats) return;

    if (stats.numberOfQuestionsCompleted === 0) {
      // Re-start questions - go to practice page
      navigate("/practice", {
        state: {
          assignmentName: "Quick Practice",
          practiceModuleIds: stats.moduleIds,
          practiceSubjectId: null,
          examBoards: [],
        },
      });
    } else if (stats.numberOfQuestionsCompleted < stats.numberOfQuestions) {
      // Continue questions - go to practice page
      navigate("/practice", {
        state: {
          assignmentName: "Quick Practice",
          practiceModuleIds: stats.moduleIds,
          practiceSubjectId: null,
          examBoards: [],
        },
      });
    } else {
      // Review completed questions - go to review page
      navigate(`/review?moduleIds=${stats.moduleIds.join(",")}`);
    }
  };

  const getPrimaryButtonText = () => {
    if (!stats) return "Review Questions";

    if (stats.numberOfQuestionsCompleted === 0) {
      return "Re-start Questions";
    } else if (stats.numberOfQuestionsCompleted < stats.numberOfQuestions) {
      return "Continue Questions";
    } else {
      return "Review Questions";
    }
  };

  const handleViewStarred = () => {
    if (stats && stats.numberOfStarredQuestions > 0) {
      navigate("/practice?starred=true", {
        state: {
          assignmentName: "Starred Questions",
          practiceModuleIds: stats.moduleIds,
          starred: true,
        },
      });
    } else {
      toast.error("You haven't starred any questions yet", { duration: 2000 });
    }
  };

  const handleViewFlagged = () => {
    if (stats && stats.numberOfFlaggedQuestions > 0) {
      // Assuming similar implementation for flagged questions
      navigate(`/review?moduleIds=${stats.moduleIds.join(",")}&flagged=true`);
    } else {
      toast.error("You haven't flagged any questions yet", { duration: 2000 });
    }
  };

  if (isLoading) {
    return (
      <div className="page-wrapper" id="ModuleStatsPage">
        <Sidebar />
        <div className="internal-page-wrapper">
          <Topbar title="Module Statistics" />
          <div
            style={{
              display: "grid",
              height: "100%",
            }}
          >
            <Loader
              size="md"
              content="Loading statistics..."
              style={{
                margin: "auto",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="page-wrapper" id="ModuleStatsPage">
        <Sidebar />
        <div className="internal-page-wrapper">
          <Topbar title="Module Statistics" />
          <main>
            <p>No statistics found for this module.</p>
          </main>
        </div>
      </div>
    );
  }

  const scorePercentage =
    stats.numberOfQuestionsCompleted > 0
      ? Math.round(
          (stats.correctlyAnsweredQuestionsCount /
            stats.numberOfQuestionsCompleted) *
            100
        )
      : 0;

  const completionPercentage = Math.round(
    (stats.numberOfQuestionsCompleted / stats.numberOfQuestions) * 100
  );

  return (
    <div className="page-wrapper" id="ModuleStatsPage">
      <Sidebar />
      <div className="internal-page-wrapper">
        <Topbar title="Module Statistics" />
        <main className="stats-main">
          <div className="stats-container">
            <h2 className="module-name">{stats.moduleName}</h2>
            <h3 className="completion-message">
              Well done, {stats.moduleIds.length === 1 ? 'module' : 'modules'} complete! 🎉
            </h3>
            <p className="completion-subtitle">
              You've finished all {stats.moduleIds.length === 1 ? "this module's" : "these modules'"} questions. Here's your
              performance summary:
            </p>

            <div className="stats-grid">
              {/* Completion Rate Chart */}
              <div className="stat-card">
                <h4>Completion Rate</h4>
                <div className="chart-container">
                  <CircularProgressbar
                    value={completionPercentage}
                    text={`${completionPercentage}%`}
                    styles={buildStyles({
                      textSize: "16px",
                      pathColor: "#316AFF",
                      textColor: "#316AFF",
                      trailColor: "#d6d6d6",
                    })}
                  />
                </div>
                <div className="stat-details">
                  <p>
                    {stats.numberOfQuestionsCompleted} / {stats.numberOfQuestions}{" "}
                    questions completed
                  </p>
                </div>
              </div>

              {/* Score Chart */}
              <div className="stat-card">
                <h4>Score</h4>
                <div className="chart-container">
                  <CircularProgressbar
                    value={scorePercentage}
                    text={`${scorePercentage}%`}
                    styles={buildStyles({
                      textSize: "16px",
                      pathColor: scorePercentage >= 70 ? "#20BB27" : "#ee9d08",
                      textColor: scorePercentage >= 70 ? "#20BB27" : "#ee9d08",
                      trailColor: "#d6d6d6",
                    })}
                  />
                </div>
                <div className="stat-details">
                  <p>
                    {stats.correctlyAnsweredQuestionsCount} correct,{" "}
                    {stats.incorrectlyAnsweredQuestionsCount} incorrect
                  </p>
                </div>
              </div>

              {/* Starred Questions Card */}
              <div
                className="stat-card clickable"
                onClick={handleViewStarred}
                style={{ cursor: stats.numberOfStarredQuestions > 0 ? "pointer" : "default" }}
              >
                <h4>Starred Questions</h4>
                <div className="chart-container">
                  <div className="number-display">
                    <span className="big-number">
                      {stats.numberOfStarredQuestions}
                    </span>
                  </div>
                </div>
                <div className="stat-details">
                  {stats.numberOfStarredQuestions > 0 ? (
                    <p className="clickable-text">Click to review</p>
                  ) : (
                    <p>No starred questions</p>
                  )}
                </div>
              </div>

              {/* Flagged Questions Card */}
              <div
                className="stat-card clickable"
                onClick={handleViewFlagged}
                style={{ cursor: stats.numberOfFlaggedQuestions > 0 ? "pointer" : "default" }}
              >
                <h4>Flagged Questions</h4>
                <div className="chart-container">
                  <div className="number-display">
                    <span className="big-number">
                      {stats.numberOfFlaggedQuestions || 0}
                    </span>
                  </div>
                </div>
                <div className="stat-details">
                  {stats.numberOfFlaggedQuestions > 0 ? (
                    <p className="clickable-text">Click to review</p>
                  ) : (
                    <p>No flagged questions</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <BootstrapButton
                variant="primary"
                size="lg"
                onClick={handlePrimaryAction}
              >
                {getPrimaryButtonText()}
              </BootstrapButton>
              <BootstrapButton
                variant="outline-danger"
                size="lg"
                onClick={handleResetProgress}
              >
                Reset Progress
              </BootstrapButton>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ModuleStatsPage;
